import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  TrackByFunction,
  ViewChild,
} from '@angular/core';
import { Commands, ParamSelections } from '@local/client-contracts';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { KeysNavigationComponent } from '@shared/components/keys-navigation.component';
import { LogService } from '@shared/services';
import { BreadcrumbsService } from '@shared/services/breadcrumbs.service';
import { CustomKeyboardEvent, KeyboardService } from '@shared/services/keyboard.service';
import { isNewCollectionCommand, isOpenUrlCommand } from '@shared/utils';
import { getModifiers, isEnterKey, KeyName, startsWihVowel, capitalCase } from '@local/ts-infra';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HubService } from 'src/app/bar/services/hub.service';
import { ParamSelectionsModel } from 'src/app/bar/services/params-selector.service';
import { TagsService } from 'src/app/bar/services/tags.service';
import { InvokeCommand } from '../../models/invoke-command.model';
import { TelemetryTrigger } from '../../models/results-types';
import { ParamSelectionsItemComponent } from './param-selections-item/param-selections-item.component';

@UntilDestroy()
@Component({
  selector: 'param-selections',
  templateUrl: './param-selections.component.html',
  styleUrls: ['./param-selections.component.scss'],
})
export class ParamSelectionsComponent
  extends KeysNavigationComponent<ParamSelections.Item, ParamSelectionsItemComponent>
  implements OnDestroy
{
  @ViewChild('view', { static: false }) viewElement: ElementRef;
  @Output() invoke = new EventEmitter<InvokeCommand>();
  @Output() destroy = new EventEmitter();

  @Input() model: ParamSelectionsModel;

  text: string;
  query: string;

  private subscriptions: Subscription[] = [];
  resourceName: string;
  currentState: any;
  autoCompleteState: boolean;

  get items(): ParamSelections.Item[] {
    return [...this.highlights, ...(this.recency || [])].filter((i) => i.view.title.text.includes(this.query || ''));
  }

  get itemHeights(): number[] {
    return new Array(this.items.length).fill(48);
  }

  get highlights(): ParamSelections.Item[] {
    return this.model.highlights;
  }

  get recency(): ParamSelections.Item[] {
    return this.model.recency;
  }

  private readonly destroy$ = new Subject<void>();

  constructor(
    logService: LogService,
    private hubService: HubService,
    cdr: ChangeDetectorRef,
    private tagsService: TagsService,
    keyboardService: KeyboardService,
    private breadcrumbsService: BreadcrumbsService
  ) {
    super(logService, cdr, keyboardService);
    this.logger = logService.scope('ParamSelectionsComponent');
  }

  ngOnInit() {
    super.ngOnInit();
    this.currentState = { ...this.hubService.state };
    this.autoCompleteState = this.hubService.autoCompleteEnabled;
    this.hubService.autoCompleteEnabled = false;
    this.hubService.searchMethod = 'Quick-Search';
    this.hubService.query = '';
    this.breadcrumbsService.navigationEnabled = false;
    this.tagsService.add({
      id: this.model.name,
      icon: this.model.icon,
      label: '',
      title: this.model.name,
      type: 'action',
    });
    this.subscriptions.push(
      this.tagsService.removed$.pipe(takeUntil(this.destroy$)).subscribe((tag) => {
        if (tag.id === this.model.name) this.destroyComponent();
      })
    );
    this.subscriptions.push(
      this.hubService.query$.pipe(untilDestroyed(this)).subscribe((query) => {
        this.query = query;
        this.cdr.markForCheck();
      })
    );
    setTimeout(() => {
      this.cdr.markForCheck();
    }, 0);
    this.resourceName = ['account', 'collection'].includes(this.model.type) ? capitalCase(this.model.type) : this.model.resourceName;
    this.text = `Select ${startsWihVowel(this.resourceName) ? 'an' : 'a'} ${this.resourceName}`;

    this.tagsService.all$.pipe(takeUntil(this.destroy$)).subscribe((tags) => {
      if (tags.length === 0) {
        this.destroyComponent();
      }
    });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.destroy$.next(null);
    this.destroy$.complete();
    this.breadcrumbsService.navigationEnabled = true;
    this.hubService.autoCompleteEnabled = this.autoCompleteState;
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions = [];
  }

  trackResult: TrackByFunction<ParamSelections.Item> = (index: number, item: ParamSelections.Item): string => item.id;

  protected handleKeys(keys: Array<KeyName>, event: CustomKeyboardEvent) {
    if (keys.length > 2) return;
    if (keys.length === 2) {
      const modifiers = getModifiers(keys);

      if (modifiers.length === 1 && modifiers[0] === 'control' && keys[0] === 'b') {
        this.goBack();
        return false;
      }
      return;
    }

    super.handleKeys(keys, event);
    if (event.propagationStopped) {
      return;
    }

    const key = keys[0];
    if (isEnterKey(key)) {
      const param = this.items[this.selectedIndex];
      if (!param) return true;
      this.onInvoke(param.view.title.onClick, param, 'keyboard');
      return false;
    }
    return true;
  }

  onItemClick(index: number): void {
    const param = this.items[index];
    this.onInvoke(param.view.title.onClick, param, 'mouse_click');
  }

  async onInvoke(command: Commands.Command, item: ParamSelections.Item, trigger: TelemetryTrigger): Promise<void> {
    if (!isOpenUrlCommand(command) && !isNewCollectionCommand(command)) return;
    const label = `${item.appId}:select_${this.resourceName}`.replace('/', '_').toLowerCase();
    const event = { target: trigger, label, resources: this.getResourceEventData([item]) };
    this.invoke.emit({ command, eventInfo: event, trigger });
  }

  private getResourceEventData(resources: ParamSelections.Item[]) {
    return resources.map((r, i) => {
      const paramId = r.id.substring(r.id.indexOf('_') + 1, r.id.length);
      return {
        id: paramId,
        list: 'action_params_list',
        type: `action-param:${paramId}`,
        position: i,
        linkId: r.linkId,
      };
    });
  }

  private destroyComponent() {
    this.destroy.emit();
  }

  goBack() {
    this.destroyComponent();
    this.tagsService.remove(this.model.name);
  }
}
